/**
 * Engagement Report Page
 *
 * GA4 page engagement metrics (time on page, scroll, conversions)
 * correlated with traffic source, landing page, device, and campaign.
 */

import { format } from 'date-fns'
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { trafficAPI } from '../services/api'
import {
  Card, CardContent, CardHeader, CardTitle,
  Button
} from '../components/ui'
import {
  RefreshCw, Loader2, AlertCircle, Search, X, Filter
} from 'lucide-react'
import { DateRangePicker } from '../components/DateRangePicker'

function formatNum(v) {
  if (v == null || isNaN(v)) return '-'
  return new Intl.NumberFormat('en-US').format(v)
}

function formatPct(v) {
  if (v == null || isNaN(v)) return '-'
  return v.toFixed(1) + '%'
}

function formatSec(v) {
  if (v == null || isNaN(v) || v === 0) return '-'
  if (v < 60) return v + 's'
  return Math.floor(v / 60) + 'm ' + Math.round(v % 60) + 's'
}

function engColor(sec) {
  if (sec == null || sec === 0) return ''
  if (sec >= 30) return 'text-green-600 font-semibold'
  if (sec >= 10) return 'text-blue-600'
  if (sec >= 3) return 'text-foreground'
  return 'text-muted-foreground'
}

export default function EngagementPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [groupBy, setGroupBy] = useState('lp') // 'lp', 'source', 'campaign'

  const now = new Date()
  const defaultFrom = new Date(now); defaultFrom.setDate(now.getDate() - 7)

  const [dateRange, setDateRange] = useState(() => {
    const s = searchParams.get('start')
    const e = searchParams.get('end')
    if (s && e) return { from: new Date(s + 'T12:00:00'), to: new Date(e + 'T12:00:00') }
    return { from: defaultFrom, to: now }
  })

  const startDate = format(dateRange.from, 'yyyy-MM-dd')
  const endDate = format(dateRange.to, 'yyyy-MM-dd')

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await trafficAPI.getEngagement({ start: startDate, end: endDate })
      if (res.data.success) setData(res.data.data)
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate])

  useEffect(() => { fetchData() }, [fetchData])

  useEffect(() => {
    setSearchParams({ start: startDate, end: endDate }, { replace: true })
  }, [startDate, endDate, setSearchParams])

  // Aggregate detail rows by grouping
  const grouped = useMemo(() => {
    const rows = data?.detail || []
    const map = {}

    for (const r of rows) {
      let key
      if (groupBy === 'source') key = r.source + ' / ' + r.medium
      else if (groupBy === 'campaign') key = r.campaign || '(not set)'
      else key = (r.host || '') + (r.landing_page || '/')

      if (!map[key]) map[key] = { key, sessions: 0, users: 0, emails: 0, purchases: 0, scrolls: 0, engSum: 0, engCount: 0, sources: new Set(), devices: new Set() }
      map[key].sessions += r.sessions
      map[key].users += r.users
      map[key].emails += r.emails
      map[key].purchases += r.purchases
      map[key].scrolls += r.scrolls
      if (r.avg_engagement_sec > 0) {
        map[key].engSum += r.avg_engagement_sec * r.sessions
        map[key].engCount += r.sessions
      }
      if (groupBy !== 'source') map[key].sources.add(r.source)
      map[key].devices.add(r.device)
    }

    return Object.values(map)
      .map(g => ({
        ...g,
        avg_engagement_sec: g.engCount > 0 ? Math.round((g.engSum / g.engCount) * 10) / 10 : null,
        cvr: g.sessions > 0 ? (g.purchases / g.sessions) * 100 : 0,
        email_rate: g.sessions > 0 ? (g.emails / g.sessions) * 100 : 0,
        scroll_rate: g.sessions > 0 ? (g.scrolls / g.sessions) * 100 : 0,
        source_list: [...g.sources].join(', '),
      }))
      .filter(g => {
        if (!searchQuery) return true
        const q = searchQuery.toLowerCase()
        return g.key.toLowerCase().includes(q) || g.source_list.toLowerCase().includes(q)
      })
      .sort((a, b) => b.sessions - a.sessions)
  }, [data, groupBy, searchQuery])

  const bySource = data?.by_source || []

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Page Engagement</h1>
          <p className="text-sm text-muted-foreground mt-1">
            GA4 engagement time, scroll depth, and conversions by source and landing page
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={(r) => { if (r?.from && r?.to) setDateRange(r) }} />
          <Button onClick={fetchData} variant="outline" size="sm" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-destructive"><CardContent className="pt-6">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="w-5 h-5" /><span>{error}</span>
          </div>
        </CardContent></Card>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {data && (
        <>
          {/* Source summary */}
          {bySource.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">By Source</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 font-medium">Source</th>
                        <th className="text-right p-3 font-medium">Users</th>
                        <th className="text-right p-3 font-medium">Pageviews</th>
                        <th className="text-right p-3 font-medium">Avg Engagement</th>
                        <th className="text-right p-3 font-medium">Scrolls</th>
                        <th className="text-right p-3 font-medium">Emails</th>
                        <th className="text-right p-3 font-medium">Purchases</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bySource.map((r, i) => (
                        <tr key={i} className="border-t hover:bg-muted/30">
                          <td className="p-3 font-medium">{r.value}</td>
                          <td className="p-3 text-right font-mono">{formatNum(r.users)}</td>
                          <td className="p-3 text-right font-mono">{formatNum(r.pageviews)}</td>
                          <td className={`p-3 text-right font-mono ${engColor(r.avg_engagement_sec)}`}>{formatSec(r.avg_engagement_sec)}</td>
                          <td className="p-3 text-right font-mono">{formatNum(r.scrolls)}</td>
                          <td className="p-3 text-right font-mono">{formatNum(r.emails)}</td>
                          <td className="p-3 text-right font-mono">{formatNum(r.purchases)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Group by + Search */}
          <Card><CardContent className="py-3 px-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <form onSubmit={(e) => { e.preventDefault(); setSearchQuery(searchInput) }} className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Filter pages or sources..."
                    className="w-full pl-9 pr-8 py-1.5 text-sm border rounded-md bg-background"
                  />
                  {searchInput && (
                    <button type="button" onClick={() => { setSearchInput(''); setSearchQuery('') }} className="absolute right-2 top-1/2 -translate-y-1/2">
                      <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                    </button>
                  )}
                </div>
                <Button type="submit" size="sm">Filter</Button>
              </form>
              <div className="flex items-center gap-1">
                <Filter className="h-4 w-4 text-muted-foreground" />
                {[
                  { value: 'lp', label: 'By LP' },
                  { value: 'source', label: 'By Source' },
                  { value: 'campaign', label: 'By Campaign' },
                ].map(opt => (
                  <Button key={opt.value} variant={groupBy === opt.value ? 'default' : 'ghost'} size="sm"
                    onClick={() => setGroupBy(opt.value)} className="h-7 text-xs">
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent></Card>

          {/* Detail table */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {groupBy === 'lp' ? 'Landing Pages' : groupBy === 'source' ? 'Sources' : 'Campaigns'} ({grouped.length})
                {loading && <Loader2 className="inline w-4 h-4 ml-2 animate-spin" />}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {grouped.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No data</p>
              ) : (
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 font-medium">{groupBy === 'lp' ? 'Landing Page' : groupBy === 'source' ? 'Source / Medium' : 'Campaign'}</th>
                        <th className="text-right p-3 font-medium">Sessions</th>
                        <th className="text-right p-3 font-medium">Avg Engage</th>
                        <th className="text-right p-3 font-medium">Scroll %</th>
                        <th className="text-right p-3 font-medium">Email %</th>
                        <th className="text-right p-3 font-medium">CVR</th>
                        <th className="text-right p-3 font-medium">Emails</th>
                        <th className="text-right p-3 font-medium">Purchases</th>
                        {groupBy !== 'source' && <th className="text-left p-3 font-medium">Sources</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {grouped.slice(0, 50).map((r, i) => (
                        <tr key={i} className="border-t hover:bg-muted/30">
                          <td className="p-3 text-xs font-mono break-words max-w-[300px]">{r.key}</td>
                          <td className="p-3 text-right font-mono">{formatNum(r.sessions)}</td>
                          <td className={`p-3 text-right font-mono ${engColor(r.avg_engagement_sec)}`}>{formatSec(r.avg_engagement_sec)}</td>
                          <td className="p-3 text-right font-mono">{formatPct(r.scroll_rate)}</td>
                          <td className="p-3 text-right font-mono">{formatPct(r.email_rate)}</td>
                          <td className="p-3 text-right font-mono">{formatPct(r.cvr)}</td>
                          <td className="p-3 text-right font-mono">{formatNum(r.emails)}</td>
                          <td className="p-3 text-right font-mono">{formatNum(r.purchases)}</td>
                          {groupBy !== 'source' && (
                            <td className="p-3 text-xs text-muted-foreground max-w-[150px] truncate" title={r.source_list}>{r.source_list}</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
